import React from "react";
import { Link } from "react-router-dom";
import { Layers, ChevronRight } from "lucide-react";
import { buildRollups } from "utilities/companyRollups";
import { useCompanyRollups } from "hooks/crm/useCompanyRollups";

/**
 * SOUND-643 Phase 3 — related records on the company record home.
 *
 * Overview was four cards of seven fields — name, type, tier, slug, id, created,
 * updated — and said nothing about what the company actually HAS. This is the
 * Salesforce move: lead with the related records and let each one be a way in.
 *
 * A count of zero renders as 0, dimmed. An UNKNOWN count renders a dash. Those
 * are different claims: zero means "we looked, there are none", a dash means
 * "this did not load" — often a legitimate 403, since a partner without
 * `mid-manager` cannot see MIDs. Collapsing the two into "0" would report
 * absence that was never established.
 */
export default function RelatedRecordsCard({ companyId }) {
  const { counts, loading } = useCompanyRollups(companyId);
  const rollups = buildRollups(companyId, counts, { loading });

  return (
    <section className="surface p-4">
      <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
        Related Records
      </h3>

      <div className="divide-y divide-border/60">
        {rollups.map((r) => (
          <Link
            key={r.key}
            to={r.href}
            className="group flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
          >
            <span className="min-w-0 truncate text-sm text-foreground">
              {r.label}
            </span>
            <span className="flex shrink-0 items-center gap-1.5">
              <span
                className={
                  r.count === null || r.empty
                    ? "text-sm tabular-nums text-muted-foreground/60"
                    : "text-sm font-medium tabular-nums text-foreground"
                }
                title={
                  r.count === null
                    ? "Not available — this list did not load"
                    : undefined
                }
              >
                {r.display}
              </span>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
